import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminPlans, useCreatePlan } from "../../hooks/use-admin.js";
import { Skeleton } from "../../components/skeleton.js";

function formatEur(cents: number): string {
  return `\u20AC${(cents / 100).toFixed(0)}`;
}

export function AdminPlansPage() {
  const { data, isLoading } = useAdminPlans();
  const createPlan = useCreatePlan();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [newWebsites, setNewWebsites] = useState("1");
  const [newScans, setNewScans] = useState("2");
  const [newPages, setNewPages] = useState("150");
  const [newHistory, setNewHistory] = useState("1");
  const [newDocs, setNewDocs] = useState(false);
  const [newRecurring, setNewRecurring] = useState(false);
  const [newStripePriceId, setNewStripePriceId] = useState("");
  const [newSortOrder, setNewSortOrder] = useState("0");

  async function handleCreate() {
    if (!newName.trim() || !newPrice) return;
    try {
      await createPlan.mutateAsync({
        name: newName.trim(),
        stripePriceId: newStripePriceId || null,
        priceEurCents: parseInt(newPrice, 10),
        maxWebsites: parseInt(newWebsites, 10) || 1,
        scansPerMonthPerWebsite: parseInt(newScans, 10) || 2,
        maxPagesPerScan: parseInt(newPages, 10) || 150,
        docsEnabled: newDocs,
        recurringEnabled: newRecurring,
        scanHistoryMonths: parseInt(newHistory, 10) || 1,
        sortOrder: parseInt(newSortOrder, 10) || 0,
      });
      setShowCreate(false);
      setNewName("");
      setNewPrice("");
      setNewStripePriceId("");
    } catch {
      // error shown via createPlan.isError
    }
  }

  return (
    <div className="product-page">
      <div className="mb-4">
        <h1 className="page-title">Plans</h1>
        <p className="page-subtitle">
          Manage plan configuration and Stripe price IDs.
        </p>
        <button
          type="button"
          className="btn btn--primary btn--sm mt-2"
          onClick={() => setShowCreate(!showCreate)}
        >
          {showCreate ? "Cancel" : "Create plan"}
        </button>
      </div>

      {showCreate && (
        <section className="product-section mb-3">
          <h2 className="section-title">New plan</h2>
          <div className="admin-form-stack">
            <label>
              <span className="text-sm">Name</span>
              <input type="text" className="form-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Pro" />
            </label>
            <label>
              <span className="text-sm">Price (EUR cents, e.g. 4900 = &euro;49)</span>
              <input type="number" className="form-input" value={newPrice} onChange={(e) => setNewPrice(e.target.value)} min="0" />
            </label>
            <label>
              <span className="text-sm">Stripe Price ID (optional)</span>
              <input type="text" className="form-input" value={newStripePriceId} onChange={(e) => setNewStripePriceId(e.target.value)} placeholder="price_..." />
            </label>
            <div className="d-flex gap-2">
              <label style={{ flex: 1 }}>
                <span className="text-sm">Max websites</span>
                <input type="number" className="form-input" value={newWebsites} onChange={(e) => setNewWebsites(e.target.value)} min="1" />
              </label>
              <label style={{ flex: 1 }}>
                <span className="text-sm">Scans/mo/site</span>
                <input type="number" className="form-input" value={newScans} onChange={(e) => setNewScans(e.target.value)} min="1" />
              </label>
              <label style={{ flex: 1 }}>
                <span className="text-sm">Pages/scan</span>
                <input type="number" className="form-input" value={newPages} onChange={(e) => setNewPages(e.target.value)} min="1" />
              </label>
            </div>
            <div className="d-flex gap-2">
              <label style={{ flex: 1 }}>
                <span className="text-sm">History (months)</span>
                <input type="number" className="form-input" value={newHistory} onChange={(e) => setNewHistory(e.target.value)} min="1" />
              </label>
              <label style={{ flex: 1 }}>
                <span className="text-sm">Sort order</span>
                <input type="number" className="form-input" value={newSortOrder} onChange={(e) => setNewSortOrder(e.target.value)} />
              </label>
            </div>
            <div className="d-flex gap-3">
              <div className="form-checkbox-row">
                <input type="checkbox" id="new-docs" checked={newDocs} onChange={(e) => setNewDocs(e.target.checked)} />
                <label htmlFor="new-docs" className="text-sm">Document scanning</label>
              </div>
              <div className="form-checkbox-row">
                <input type="checkbox" id="new-recurring" checked={newRecurring} onChange={(e) => setNewRecurring(e.target.checked)} />
                <label htmlFor="new-recurring" className="text-sm">Recurring monitoring</label>
              </div>
            </div>
            <button
              type="button"
              className="btn btn--primary"
              disabled={createPlan.isPending || !newName.trim() || !newPrice}
              onClick={() => void handleCreate()}
            >
              {createPlan.isPending ? "Creating\u2026" : "Create plan"}
            </button>
            {createPlan.isSuccess && <p className="text-muted text-sm">Plan created.</p>}
            {createPlan.isError && (
              <p className="alert alert--error text-sm">
                {createPlan.error instanceof Error ? createPlan.error.message : "Failed"}
              </p>
            )}
          </div>
        </section>
      )}

      {isLoading && <Skeleton lines={3} />}

      {data && (
        <section className="product-section">
          {/* Desktop Table View */}
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Price</th>
                  <th>Stripe Price ID</th>
                  <th className="text-center">
                    Websites
                  </th>
                  <th className="text-center">
                    Pages
                  </th>
                  <th className="text-center">
                    Scans/mo
                  </th>
                  <th className="text-center">
                    History
                  </th>
                  <th className="text-center">
                    Docs
                  </th>
                  <th className="text-center">
                    Recurring
                  </th>
                  <th className="text-center">
                    Subscribers
                  </th>
                  <th className="text-center">
                    Status
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.plans.map((plan) => (
                  <tr
                    key={plan.id}
                    className={plan.is_active ? "" : "text-muted"}
                  >
                    <td className="fw-600">
                      {plan.name}
                    </td>
                    <td>
                      {plan.price_eur_cents > 0
                        ? `${formatEur(plan.price_eur_cents)}/mo`
                        : "Custom"}
                    </td>
                    <td>
                      {plan.stripe_price_id ? (
                        <code className="badge badge--advisory">
                          {plan.stripe_price_id.length > 24
                            ? `${plan.stripe_price_id.slice(0, 24)}\u2026`
                            : plan.stripe_price_id}
                        </code>
                      ) : (
                        <span className="warning-badge">NOT CONFIGURED</span>
                      )}
                    </td>
                    <td className="text-center">
                      {plan.max_websites >= 9999 ? "\u221E" : plan.max_websites}
                    </td>
                    <td className="text-center">
                      {plan.max_pages_per_scan >= 99999
                        ? "\u221E"
                        : plan.max_pages_per_scan.toLocaleString()}
                    </td>
                    <td className="text-center">
                      {plan.scans_per_month_per_website >= 9999
                        ? "\u221E"
                        : plan.scans_per_month_per_website}
                    </td>
                    <td className="text-center">
                      {plan.scan_history_months >= 9999
                        ? "\u221E"
                        : `${plan.scan_history_months}mo`}
                    </td>
                    <td className="text-center">
                      {plan.docs_enabled ? "\u2713" : "\u2014"}
                    </td>
                    <td className="text-center">
                      {plan.recurring_enabled ? "\u2713" : "\u2014"}
                    </td>
                    <td className="text-center">
                      {plan.subscriberCount}
                    </td>
                    <td className="text-center">
                      {plan.is_active ? (
                        <span className="text-success">Active</span>
                      ) : (
                        <span className="text-muted">Inactive</span>
                      )}
                    </td>
                    <td>
                      <Link
                        to={`/admin/plans/${plan.id}`}
                        className="btn btn--secondary btn--sm"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.plans.length === 0 && (
            <p className="text-muted text-center py-2">
              No plans found.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
